/**
 * Custom edge that combines two routing modes:
 *
 * 1. **Default — smoothstep** with right-angle bends through the source and
 *    target handles. Reduces overlap visually compared to a straight bezier
 *    when many lines share the same neighbourhood.
 * 2. **Waypoints** — once the user clicks-and-drags on the line, the path
 *    becomes an orthogonal polyline through `data.waypoints` (in absolute
 *    flow coordinates). Every waypoint renders as a draggable circle when
 *    the edge is selected; mid-segment grab-handles let the user add new
 *    waypoints by clicking + dragging anywhere on the line.
 *
 * Persistence: waypoint changes go through `setRelationWaypointsOnCanvas`,
 * which writes them into the relation's view_state on the active diagram.
 */
import {
  BaseEdge,
  EdgeLabelRenderer,
  type EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  Position,
  useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { useKeymap } from "../../state/keymap";
import { claimObjectLock, releaseObjectLock } from "../../state/objectLocks";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import {
  angleFromPoint,
  angleToPosition,
  nearestBorderAngle,
  pointFromAngle,
  type Rect,
  rectFromHandle,
} from "./angleUtils";
import { edgeLabelFocusId } from "./focusNavigation";

interface RoutedEdgeData {
  waypoints?: Array<{ x: number; y: number }> | undefined;
  routing?: "smoothstep" | "straight" | "bezier" | "step" | "manual" | undefined;
  labelOffset?: { x: number; y: number } | undefined;
  /** Set via Cmd/Ctrl-drag on the label — pixel cap on the label
   *  container's width, forces wrap when exceeded. */
  labelMaxWidth?: number | undefined;
  /** CSS text-align voor de tekst binnen het edge-label-blok.
   *  Default = "center" (huidig gedrag). Gezet via canvas-toolbar. */
  labelTextAlign?: "left" | "center" | "right" | undefined;
  /** Word-wrap-strategie voor lange edge-labels. Zie NodeStyle.labelWrap. */
  labelWrap?: "hyphenate" | "break-anywhere" | undefined;
  sourceAngle?: number | undefined;
  targetAngle?: number | undefined;
  /**
   * Set door Canvas wanneer dit endpoint via collapse re-anchored is op
   * een ancestor-container i.p.v. de oorspronkelijke (nu hidden)
   * instance. RoutedEdge berekent dan zelf een centrum-naar-centrum-hoek
   * zodat het attachment-punt op de container-rand uitkomt waar de
   * denkbeeldige rechte lijn naar het andere endpoint de rand zou
   * kruisen — i.p.v. dat React Flow een willekeurige default-handle
   * (bv. top-midden) kiest.
   */
  sourceReAnchored?: boolean;
  targetReAnchored?: boolean;
  lineColor?: string | undefined;
  sourceSize?: { w: number; h: number } | undefined;
  targetSize?: { w: number; h: number } | undefined;
  /** Optional small subtitle under the edge label — set on C4 views
   *  from the relation's `technology` attribute. */
  technology?: string | undefined;
  /**
   * Sharp Concept-Model rendering: in plaats van één naam in het midden
   * tonen we forward_name nabij source en reverse_name nabij target,
   * geroteerd om met de lijn mee te lopen. Set door Canvas wanneer de
   * relatie het sharp.ConceptAssociation/IdentifyingAssociation
   * stereotype draagt op een sharp.concept-model of
   * sharp.detailed-concept view. Bij gezet wordt de standaard `label`
   * onderdrukt.
   */
  dualEndLabels?: { forward: string; reverse: string } | undefined;
  /** Source-zijde label-inset langs de lijn (px). Default 14.
   *  Legacy — sinds vrije 2D-offset niet meer actief gebruikt; blijft
   *  voor backwards-compat van bestaande viewState. */
  sourceLabelInset?: number | undefined;
  /** Target-zijde label-inset langs de lijn (px). Default 14. Legacy. */
  targetLabelInset?: number | undefined;
  /** Max-breedte voor forward/reverse-label. Wraps tekst boven die
   *  breedte. `undefined` = intrinsieke breedte. */
  sourceLabelWidth?: number | undefined;
  targetLabelWidth?: number | undefined;
  /** Vrije 2D-offset in flow-coords t.o.v. default-positie nabij het
   *  uiteinde — analoog aan labelOffset op het standaard label. */
  sourceLabelOffset?: { x: number; y: number } | undefined;
  targetLabelOffset?: { x: number; y: number } | undefined;
  [key: string]: unknown;
}

/**
 * Push a single user-undo entry that restores the relation's waypoint
 * list. Used by drag-end / single-point-drag-end / manual-clear paths
 * so Cmd+Z reverts a bend gesture in one step (instead of one entry per
 * mousemove tick).
 */
function pushWaypointsUndo(
  workspaceId: string,
  branchId: string,
  relationId: string,
  before: Array<{ x: number; y: number }>,
  after: Array<{ x: number; y: number }>,
): void {
  if (JSON.stringify(before) === JSON.stringify(after)) return;
  const apply = (snapshot: Array<{ x: number; y: number }>) => {
    useWorkspaceData
      .getState()
      .setRelationWaypointsOnCanvas(
        workspaceId,
        branchId,
        relationId,
        snapshot.length === 0 ? null : snapshot,
      );
  };
  useWorkspaceData.getState().pushUserUndo({
    label: "Lijn-knikpunten",
    undo: () => apply(before),
    redo: () => apply(after),
  });
}

/**
 * Push a single undo entry for a source/target angle change. Captures the
 * before/after pair set at the start and end of an attachment-circle drag,
 * so Cmd+Z reverts the whole gesture instead of one entry per mousemove.
 */
function pushAngleUndo(
  workspaceId: string,
  branchId: string,
  relationId: string,
  before: { sourceAngle: number | undefined; targetAngle: number | undefined },
  after: { sourceAngle: number | undefined; targetAngle: number | undefined },
): void {
  if (before.sourceAngle === after.sourceAngle && before.targetAngle === after.targetAngle) return;
  // null tells setRelationAnglesOnCanvas to explicitly clear; we use it
  // when the snapshot's value was undefined, so undo/redo can restore that.
  const toArg = (v: number | undefined): number | null => (v === undefined ? null : v);
  const apply = (snapshot: {
    sourceAngle: number | undefined;
    targetAngle: number | undefined;
  }) => {
    useWorkspaceData
      .getState()
      .setRelationAnglesOnCanvas(
        workspaceId,
        branchId,
        relationId,
        toArg(snapshot.sourceAngle),
        toArg(snapshot.targetAngle),
      );
  };
  useWorkspaceData.getState().pushUserUndo({
    label: "Lijn-aanhechting",
    undo: () => apply(before),
    redo: () => apply(after),
  });
}

/**
 * Same idea as `pushWaypointsUndo` but for the label-offset drag.
 */
function pushLabelOffsetUndo(
  workspaceId: string,
  branchId: string,
  relationId: string,
  before: { x: number; y: number },
  after: { x: number; y: number },
): void {
  if (before.x === after.x && before.y === after.y) return;
  const apply = (offset: { x: number; y: number } | null) => {
    useWorkspaceData
      .getState()
      .setRelationLabelOffsetOnCanvas(workspaceId, branchId, relationId, offset);
  };
  useWorkspaceData.getState().pushUserUndo({
    label: "Label verplaatsen",
    undo: () => apply(before.x === 0 && before.y === 0 ? null : before),
    redo: () => apply(after.x === 0 && after.y === 0 ? null : after),
  });
}

interface Point {
  x: number;
  y: number;
}

/**
 * Read the live geometry of a node from the rendered DOM. Returns
 * the visible shape's bounding rect in flow coordinates plus its CSS
 * border-radius. Returns `null` before the node has mounted.
 *
 * IMPORTANT: only safe to use during USER INTERACTION (drag handlers,
 * clicks). Calling this during render produces stale data — React's
 * commit hasn't happened yet, so the DOM still has previous positions.
 * For render use `rectFromHandle` (props-driven, always current) and
 * `readNodeBorderRadius` for the radius.
 */
function readLiveNodeRect(
  reactFlow: ReturnType<typeof useReactFlow>,
  nodeId: string,
): { x: number; y: number; w: number; h: number; radius: number } | null {
  if (typeof document === "undefined") return null;
  const wrapper = document.querySelector(
    `.react-flow__node[data-id="${nodeId.replace(/(["'\\])/g, "\\$1")}"]`,
  ) as HTMLElement | null;
  if (!wrapper) return null;
  const visual = (wrapper.querySelector("[data-concept-shape]") as HTMLElement | null) ?? wrapper;
  const r = visual.getBoundingClientRect();
  const tl = reactFlow.screenToFlowPosition({ x: r.left, y: r.top });
  const br = reactFlow.screenToFlowPosition({ x: r.right, y: r.bottom });
  const radius = Number.parseFloat(window.getComputedStyle(visual).borderTopLeftRadius) || 0;
  return { x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y, radius };
}

/**
 * Read just the visual element's border-radius from the DOM. Safe to call
 * during render: border-radius is set per-aspect and never changes between
 * renders, so even pre-commit DOM has the right value.
 */
function readNodeBorderRadius(nodeId: string): number {
  if (typeof document === "undefined") return 0;
  const wrapper = document.querySelector(
    `.react-flow__node[data-id="${nodeId.replace(/(["'\\])/g, "\\$1")}"]`,
  ) as HTMLElement | null;
  if (!wrapper) return 0;
  const visual = (wrapper.querySelector("[data-concept-shape]") as HTMLElement | null) ?? wrapper;
  return Number.parseFloat(window.getComputedStyle(visual).borderTopLeftRadius) || 0;
}

function buildPolylinePath(points: Point[]): string {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  let p = `M ${first?.x},${first?.y}`;
  for (const pt of rest) p += ` L ${pt.x},${pt.y}`;
  return p;
}

/**
 * Find the segment index closest to (px, py) in a polyline `points`. The
 * insertion index is the position of the *second* endpoint of that
 * segment, which is where the new waypoint should go in the array
 * (sliced into the existing waypoints, not the source/target endpoints).
 */
function nearestSegmentInsertIndex(points: Point[], px: number, py: number): number {
  let bestIdx = 1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 === 0 ? 0 : ((px - a.x) * dx + (py - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    const ddx = px - cx;
    const ddy = py - cy;
    const d2 = ddx * ddx + ddy * ddy;
    if (d2 < bestDist) {
      bestDist = d2;
      bestIdx = i + 1;
    }
  }
  return bestIdx;
}

/**
 * Tijd-tabel voor dblclick-detectie per edge. Module-level (niet useRef)
 * zodat 't waarde overleeft wanneer React-Flow de RoutedEdge-component
 * unmount/remount tussen twee mousedowns op dezelfde edge (gebeurt door
 * applySelectedStyle die een nieuw edge-object produceert na de eerste
 * click). useRef wordt dan gereset → dblclick-window misgelopen.
 *
 * Key = edge-id (= viewState entry-key). TTL: oudste entries vervallen
 * vanzelf bij overschrijven; geen explicit-cleanup nodig.
 */
const lastMouseDownByEdgeId = new Map<string, number>();

export function RoutedEdge(props: EdgeProps) {
  const {
    id,
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerStart,
    markerEnd,
    // BEWUST GENEGEERD — React Flow's interne edge-selection-store
    // synct niet betrouwbaar met de `selected`-flag op de edges-prop
    // wanneer wij selectie programmatisch zetten (via useUi → mirror →
    // localEdges) i.p.v. via RF's eigen click-handler of `onEdgesChange
    // `-select-changes. Resultaat: prop blijft `true` ook nadat we 'm
    // via setSelectedRelationIds geleegd hebben. Wij leiden `selected`
    // daarom uit useUi af (zie hieronder); dat is sowieso onze single
    // source of truth voor edge-selectie.
    label,
    labelStyle,
    labelBgStyle,
    labelBgPadding,
    data,
  } = props;
  // Multi-instance: `id` is de viewState entry-key (UUID voor multi-
  // instance, relation.id voor canonical). `data.relationId` is de
  // domain relation-id (gezet door edge-derivation in Canvas.tsx).
  // Voor AttributePanel-routing en selectRelation gebruiken we de
  // domain-id; voor visuele highlight + per-instance state gebruiken
  // we entry-key. Component-scope-constante zodat alle handlers 'm
  // delen.
  const relationId =
    typeof (data as { relationId?: unknown } | undefined)?.relationId === "string"
      ? (data as { relationId: string }).relationId
      : id;
  const reactFlow = useReactFlow();
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: selecteren mag, buigen, verslepen en hernoemen niet.
  const readOnly = useSession((s) => s.readOnly);
  const selectedWaypoint = useUi((s) => s.selectedWaypoint);
  const selectedInstanceId = useUi((s) => s.selectedInstanceId);
  // Eigen `selected`-bepaling — zie comment hierboven. Subscribed-selectors
  // op de twee useUi-velden zodat de component re-rendert wanneer er
  // ergens anders (canvas Cmd+klik, GridPane-rij-selectie, Explorer)
  // wijzigingen plaatsvinden.
  // FIX (2026-06-07): vergelijk de RELATIE-id, niet de edge-instance-id.
  // `selectedRelationIds` / `selectedRelationId` bevatten relatie-ids, maar
  // sinds de viewState-key-unificatie (#40, "all UUIDs") is `id` de
  // instance-entry-key (een UUID) ≠ relatie-id. Daardoor was `selected`
  // altijd false → de bewerk-handles (aanhechtpunten + waypoints) renderden
  // nooit. `relationId` (regel ~325) is de echte relatie-id van deze edge.
  //
  // AANVULLING (2026-09-08): vergelijken op de relatie-id alléén lichtte
  // ÉLK voorkomen van die relatie op. Op een diagram waar dezelfde reeks
  // drie keer staat — een veelgebruikte manier om een verhaal op te bouwen —
  // trok de modelleur een lasso om één rij en zag hij de lijnen in de andere
  // twee rijen mee oplichten, inclusief hun bewerk-handles. De selectie kon
  // hij dus niet vertrouwen, en alles wat erop volgde raakte te veel.
  //
  // `selectedEdgeIds` bevat de entry-sleutels van de voorkomens die de
  // modelleur werkelijk aanwees (lasso, Cmd+klik, contextmenu). Is die
  // gevuld, dan is dát de waarheid en vergelijken we op `id` — de sleutel
  // van dít voorkomen. Is 'ie leeg, dan komt de selectie ergens anders
  // vandaan (Explorer, Relaties-paneel, GridPane) en is "alle voorkomens
  // van deze relatie" juist de bedoeling; dan blijft de vergelijking op de
  // relatie-id staan.
  const selected = useUi((s) =>
    s.selectedEdgeIds.length > 0
      ? s.selectedEdgeIds.includes(id)
      : s.selectedRelationIds.includes(relationId) || s.selectedRelationId === relationId,
  );

  // Inline label-edit — symmetrisch met ConceptNode. Dubbelklik op
  // het edge-label opent een input in de actieve taal; Enter of blur
  // persisteren via patchRelation én triggeren auto-translate naar de
  // andere taal (zelfde policy als BilingualField in AttributePanel).
  // Esc cancelt.
  const { t, i18n } = useTranslation();
  const editLang = i18n.language;
  const editLangKey: "nl" | "en" = editLang === "en" ? "en" : "nl";
  const [editing, setEditing] = useState(false);
  /** Het relatie-id waarvan wíj het slot vasthouden; `null` als we 'm niet
   *  gekregen hebben. De gerenderde edge-id kan een instance-sleutel zijn, dus
   *  bewaren we het id dat we werkelijk geclaimd hebben. */
  const lockHeld = useRef<string | null>(null);
  const [draft, setDraft] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) editInputRef.current?.select();
  }, [editing]);
  const beginLabelEdit = () => {
    if (useSession.getState().readOnly) return;
    const relation = useWorkspaceData.getState().store.getRelation(id);
    if (!relation) return;
    setDraft(relation.label[editLangKey] ?? "");
    setEditing(true);

    // Slot ernáást, niet ervóór — zie de toelichting in `ConceptNode.tsx`.
    const target = relation.id;
    void claimObjectLock(target).then((denial) => {
      if (!denial) {
        lockHeld.current = target;
        return;
      }
      setEditing(false);
      useWorkspaceData.setState({
        lastError: denial.byBatch
          ? t("locks.heldByBatch")
          : t("locks.heldBy", { holder: denial.holder }),
      });
    });
  };

  /** Vrijgeven zodra het hernoemen klaar is — via Enter, Escape of blur. */
  const endLabelEdit = () => {
    const target = lockHeld.current;
    if (!target) return;
    lockHeld.current = null;
    releaseObjectLock(target);
  };
  // F2-shortcut bridge: Editor.tsx dispatcht `ae:rename-relation` met
  // de relation-id. Identiek aan ConceptNode's bridge — start dezelfde
  // inline-edit als dubbelklik op het label.
  useEffect(() => {
    const onRename = (e: Event) => {
      const detail = (e as CustomEvent<{ relationId: string }>).detail;
      if (!detail || detail.relationId !== id) return;
      beginLabelEdit();
    };
    window.addEventListener("ae:rename-relation", onRename);
    return () => window.removeEventListener("ae:rename-relation", onRename);
    // beginLabelEdit gebruikt id en store; geen reactive deps nodig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editLangKey]);
  const persistLabelEdit = async () => {
    if (!editing) return;
    setEditing(false);
    endLabelEdit();
    if (!workspaceId || !branchId) return;
    const relation = useWorkspaceData.getState().store.getRelation(id);
    if (!relation) return;
    const next = draft.trim();
    if (next === (relation.label[editLangKey] ?? "").trim()) return;
    const otherLang: "nl" | "en" = editLangKey === "nl" ? "en" : "nl";
    const counterpartBefore = relation.label[otherLang] ?? "";
    await useWorkspaceData.getState().patchRelation(workspaceId, branchId, id, {
      label: { ...relation.label, [editLangKey]: next },
    });
    // Auto-translate naar de andere taal als die nog identiek is aan
    // de baseline (= niemand heeft 'm tussentijds aangeraakt). Zelfde
    // last-edit-wins regel als BilingualField.
    if (next) {
      try {
        const res = await api.translate({
          source_lang: editLangKey,
          target_lang: otherLang,
          text: next,
          kind: "name",
        });
        const live = useWorkspaceData.getState().store.getRelation(id);
        if (live && (live.label[otherLang] ?? "") === counterpartBefore) {
          await useWorkspaceData.getState().patchRelation(workspaceId, branchId, id, {
            label: { ...live.label, [otherLang]: res.text },
          });
        }
      } catch (err) {
        // 412 = no Anthropic sign-in configured; silently mirror the source.
        if (err instanceof ApiError && err.status === 412) {
          const live = useWorkspaceData.getState().store.getRelation(id);
          if (live && (live.label[otherLang] ?? "") === counterpartBefore) {
            await useWorkspaceData.getState().patchRelation(workspaceId, branchId, id, {
              label: { ...live.label, [otherLang]: next },
            });
          }
        }
        // Andere errors zijn netwerk/etc — silently swallow; user can
        // nog steeds handmatig de andere taal invullen via AttributePanel.
      }
    }
    // Duplicaat-check op COMMIT (besluit 2026-06-18): nu de naam definitief is
    // (na de eventuele auto-translate). De patch-hook slaat label-only-
    // wijzigingen over om niet midden in het typen te vragen.
    await useWorkspaceData.getState().checkRelationDuplicate(workspaceId, branchId, id);
  };

  const d = data as RoutedEdgeData | undefined;
  const waypoints = (d?.waypoints ?? []).slice();
  // Default-routing voor nieuwe lijnen is "straight" (sinds 2026-05-10)
  // — recht-getekende lijn met directe verbinding tussen endpoints i.p.v.
  // de afgerond-rechthoekige smoothstep-curve. User kan via toolbar
  // (`Lijn-knikpunten`-dropdown) elk gewenst type kiezen; die keuze
  // wordt opgeslagen in viewState.routing en wint via `d?.routing`.
  const routing = d?.routing ?? "straight";
  const labelOffset = d?.labelOffset ?? { x: 0, y: 0 };
  const sourceAngle = typeof d?.sourceAngle === "number" ? d.sourceAngle : undefined;
  const targetAngle = typeof d?.targetAngle === "number" ? d.targetAngle : undefined;
  const sourceSize = d?.sourceSize as { w: number; h: number } | undefined;
  const targetSize = d?.targetSize as { w: number; h: number } | undefined;
  // Size resolution chain — most-reliable first:
  //   1. Live DOM bounding-rect (matches what the user actually sees,
  //      including auto-sized nodes that grew larger than the default
  //      due to descriptions, stereotype subtitles, etc.)
  //   2. React Flow's `measured` (settles after first paint)
  //   3. viewState-stored width/height (only set on explicit resize)
  //   4. 140×60 fallback
  // Without (1) the angle-based attachment maths landed at a wrong
  // border position whenever the rendered node was bigger than 140×60
  // (very common on C4 views) — orange dot floating in space.
  const sourceNode = reactFlow.getNode(source);
  const targetNode = reactFlow.getNode(target);
  const sourceLive = readLiveNodeRect(reactFlow, source);
  const targetLive = readLiveNodeRect(reactFlow, target);
  const sw = sourceLive?.w ?? sourceNode?.measured?.width ?? sourceSize?.w ?? 140;
  const sh = sourceLive?.h ?? sourceNode?.measured?.height ?? sourceSize?.h ?? 60;
  const tw2 = targetLive?.w ?? targetNode?.measured?.width ?? targetSize?.w ?? 140;
  const th2 = targetLive?.h ?? targetNode?.measured?.height ?? targetSize?.h ?? 60;

  // When an angle is stored for an endpoint, override the attachment point
  // to the exact border position that `pointFromAngle` computes. We use
  // rectFromHandle (driven by React Flow's current props, so always
  // up-to-date) and read border-radius from the DOM — radius is stable
  // across renders so a 1-frame stale DOM is fine, while rect dimensions
  // would not be (DOM lags during layout updates → wrong endpoints).
  //
  // Bij een re-anchored endpoint (collapse): Canvas levert in normale
  // gevallen al een uit-viewState-berekende hoek (van container-center
  // naar het ORIGINELE endpoint center via de containerId-chain) als
  // sourceAngle/targetAngle, zodat de lijn op de container-rand landt
  // waar de inner-instance "zat". Onderstaande compute-from-other-endpoint
  // fallback kicks in alleen wanneer die chain niet bepaalbaar is
  // (zeldzaam — bv. orphaned viewState zonder containerId-pad). Dan
  // landt de lijn nog steeds op een zinnig punt: waar een denkbeeldige
  // rechte lijn naar de andere node de container-rand zou kruisen.
  // sourceLive.x/y is de werkelijke top-left van de node in flow-coords
  // (gemeten via DOM-getBoundingClientRect + screenToFlowPosition).
  // Gebruik die wanneer beschikbaar — sluit aan op wat de gebruiker
  // visueel ziet, ongeacht welk handle React Flow voor de edge-anker
  // gebruikt. Vooral nodig voor corner-handles (top-left, bottom-right
  // etc.) waar `rectFromHandle`'s terugrekening uit (sourceX, sourceY,
  // sourcePosition) de rect ~w/2 verschuift omdat 't aanneemt dat 't
  // handle op het MIDDEN van de positie-edge zit — niet op een hoek.
  // Fallback op rectFromHandle voor edge-cases waar de DOM nog niet
  // gerenderd is (eerste frame na mount).
  const sourceRect: Rect = sourceLive
    ? { x: sourceLive.x, y: sourceLive.y, w: sw, h: sh, radius: readNodeBorderRadius(source) }
    : {
        ...rectFromHandle(sourceX, sourceY, sourcePosition, sw, sh),
        radius: readNodeBorderRadius(source),
      };
  const targetRect: Rect = targetLive
    ? { x: targetLive.x, y: targetLive.y, w: tw2, h: th2, radius: readNodeBorderRadius(target) }
    : {
        ...rectFromHandle(targetX, targetY, targetPosition, tw2, th2),
        radius: readNodeBorderRadius(target),
      };
  const sourceCenter = { x: sourceRect.x + sourceRect.w / 2, y: sourceRect.y + sourceRect.h / 2 };
  const targetCenter = { x: targetRect.x + targetRect.w / 2, y: targetRect.y + targetRect.h / 2 };

  let effectiveSourceAngle = sourceAngle;
  if (effectiveSourceAngle === undefined && d?.sourceReAnchored) {
    effectiveSourceAngle = angleFromPoint(sourceCenter, targetCenter);
  }
  let effectiveTargetAngle = targetAngle;
  if (effectiveTargetAngle === undefined && d?.targetReAnchored) {
    effectiveTargetAngle = angleFromPoint(targetCenter, sourceCenter);
  }

  // M6.5 floating-edge default: wanneer de gebruiker geen expliciete
  // sourceAngle/targetAngle heeft gezet, kies het aanhechtingspunt dat
  // visueel het dichtst bij het andere endpoint ligt. Zonder dit valt
  // de edge terug op de standaard top/bottom/left/right-handle die
  // React Flow voor de node-handles oplevert — vaak suboptimaal als
  // de twee nodes diagonaal van elkaar staan. Met waypoints wijst de
  // richting naar de eerste/laatste waypoint zodat de polyline-bend
  // hoek-correct landt.
  if (effectiveSourceAngle === undefined) {
    const towards = waypoints.length > 0 ? (waypoints[0] as Point) : targetCenter;
    effectiveSourceAngle = angleFromPoint(sourceCenter, towards);
  }
  if (effectiveTargetAngle === undefined) {
    const towards =
      waypoints.length > 0 ? (waypoints[waypoints.length - 1] as Point) : sourceCenter;
    effectiveTargetAngle = angleFromPoint(targetCenter, towards);
  }

  let effSourceX = sourceX;
  let effSourceY = sourceY;
  let effSourcePosition = sourcePosition;
  if (effectiveSourceAngle !== undefined) {
    const pt = pointFromAngle(sourceRect, effectiveSourceAngle);
    effSourceX = pt.x;
    effSourceY = pt.y;
    effSourcePosition = angleToPosition(effectiveSourceAngle);
  }
  let effTargetX = targetX;
  let effTargetY = targetY;
  let effTargetPosition = targetPosition;
  if (effectiveTargetAngle !== undefined) {
    const pt = pointFromAngle(targetRect, effectiveTargetAngle);
    effTargetX = pt.x;
    effTargetY = pt.y;
    effTargetPosition = angleToPosition(effectiveTargetAngle);
  }

  // Build the path. With waypoints present we honour them as a polyline
  // through (source, ...waypoints, target). Otherwise we delegate to one
  // of React Flow's path routers based on the per-edge routing setting.
  let edgePath: string;
  let labelX: number;
  let labelY: number;
  if (waypoints.length > 0) {
    const points: Point[] = [
      { x: effSourceX, y: effSourceY },
      ...waypoints,
      { x: effTargetX, y: effTargetY },
    ];
    edgePath = buildPolylinePath(points);
    const mid = points[Math.floor(points.length / 2)] ?? { x: effSourceX, y: effSourceY };
    labelX = mid.x;
    labelY = mid.y;
  } else if (routing === "straight") {
    const [path, lx, ly] = getStraightPath({
      sourceX: effSourceX,
      sourceY: effSourceY,
      targetX: effTargetX,
      targetY: effTargetY,
    });
    edgePath = path;
    labelX = lx;
    labelY = ly;
  } else if (routing === "bezier") {
    const [path, lx, ly] = getBezierPath({
      sourceX: effSourceX,
      sourceY: effSourceY,
      sourcePosition: effSourcePosition,
      targetX: effTargetX,
      targetY: effTargetY,
      targetPosition: effTargetPosition,
    });
    edgePath = path;
    labelX = lx;
    labelY = ly;
  } else if (routing === "step") {
    const [path, lx, ly] = getSmoothStepPath({
      sourceX: effSourceX,
      sourceY: effSourceY,
      sourcePosition: effSourcePosition,
      targetX: effTargetX,
      targetY: effTargetY,
      targetPosition: effTargetPosition,
      borderRadius: 0,
    });
    edgePath = path;
    labelX = lx;
    labelY = ly;
  } else {
    const [path, lx, ly] = getSmoothStepPath({
      sourceX: effSourceX,
      sourceY: effSourceY,
      sourcePosition: effSourcePosition,
      targetX: effTargetX,
      targetY: effTargetY,
      targetPosition: effTargetPosition,
    });
    edgePath = path;
    labelX = lx;
    labelY = ly;
  }

  // Het label hoort midden op de zichtbare lijn, en een pijlpunt is geen lijn.
  // De routers geven het midden van het hele pad terug, pijlpunt inbegrepen,
  // waardoor het label naar de pijl toe schuift — zichtbaar zodra twee
  // objecten dicht bij elkaar staan en de lijn kort is. We schuiven 'm daarom
  // een halve pijlpunt terug, langs de richting van de lijn.
  //
  // Alleen wanneer één van beide uiteinden een marker draagt: staan er twee
  // (of geen), dan is het midden al het midden.
  if (!markerEnd !== !markerStart) {
    const dx = effTargetX - effSourceX;
    const dy = effTargetY - effSourceY;
    const lengte = Math.hypot(dx, dy);
    if (lengte > 1) {
      // Onze markers zijn 8 tot 14 eenheden lang; 12 is de gangbare pijl.
      const halvePijlpunt = 6;
      const naarDeBron = markerEnd ? -1 : 1;
      labelX += (dx / lengte) * halvePijlpunt * naarDeBron;
      labelY += (dy / lengte) * halvePijlpunt * naarDeBron;
    }
  }

  /**
   * Mousedown on the edge body has three modes:
   *
   * - **Unselected + plain click**: select this edge as single-focus. The
   *   wide invisible hit-overlay sits on top of the real BaseEdge path,
   *   so the framework's `onEdgeClick` never fires — we replicate that
   *   here manually.
   * - **Unselected + modifier (Cmd / Ctrl / Alt / Shift)** OR **selected +
   *   modifier**: toggle this edge in/out of the multi-selection without
   *   touching concept-multi. Mirrors `Canvas.onEdgeClick` for nodes that
   *   DO route through the framework. Both paths swallow the synthesized
   *   `click` event that the browser fires after mouseup, otherwise React
   *   Flow re-emits an `onSelectionChange{edges:0}` on mouseup that
   *   un-does our selection (the "flash" bug, 2026-05-22).
   * - **Selected + plain click + drag**: insert a waypoint at the original
   *   click position and let the user pull the bend with their cursor.
   *   A pure click (no movement) does NOT insert a waypoint, so the user
   *   can still click a selected edge without accidentally bending it.
   */
  const onLineMouseDown = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      if (e.button !== 0) return;
      if (!workspaceId || !branchId) return;

      const additive = e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;

      // ── Pad 1 + 2: pure selectie-mutatie, geen drag ──
      // Eén-shot click-swallower zoals de rubber-band ook gebruikt
      // (Canvas.tsx:1867). Zonder dit fired RF op mouseup een
      // `onSelectionChange{edges:0}` die de net-gezette selectie weer
      // weghaalt → "flash"-bug bij re-selecteren van een edge na een
      // eerdere deselect.
      const swallowSynthClick = () => {
        const swallow = (cev: MouseEvent) => {
          cev.stopPropagation();
          cev.stopImmediatePropagation();
          cev.preventDefault();
          window.removeEventListener("click", swallow, true);
        };
        window.addEventListener("click", swallow, true);
        setTimeout(() => window.removeEventListener("click", swallow, true), 0);
      };

      // Manuele dblclick-detectie via module-level Map (survives
      // React-Flow's edge-remount tussen clicks; zie comment bij
      // lastMouseDownByEdgeId-declaratie). Moet NÁ swallowSynthClick
      // staan.
      const now = Date.now();
      const lastTime = lastMouseDownByEdgeId.get(id) ?? 0;
      const isDoubleClick = !additive && now - lastTime < 300;
      lastMouseDownByEdgeId.set(id, now);
      if (isDoubleClick) {
        e.preventDefault();
        e.stopPropagation();
        swallowSynthClick();
        const ui = useUi.getState();
        ui.selectRelation(relationId);
        useUi.setState({ selectedRelationInstanceId: id });
        ui.setSelectedEdgeIds([id]);
        ui.setAttributePanelOpen(true);
        return;
      }

      if (additive) {
        // Multi-select-mode: toggle deze edge in/uit de multi-sets zonder
        // concept-multi te raken. selectedEdgeIds = entry-keys (per-
        // instance visuele highlight); selectedRelationIds = relation-
        // ids (voor AttributePanel/Toolbar bulk).
        e.stopPropagation();
        e.preventDefault();
        swallowSynthClick();
        const ui = useUi.getState();
        const nextEdges = new Set(ui.selectedEdgeIds);
        const nextRels = new Set(ui.selectedRelationIds);
        const wasInSet = nextEdges.has(id);
        if (wasInSet) {
          nextEdges.delete(id);
          // Remove relation-id only if no other selected edge maps to it.
          // (Conservative: meerdere instances van dezelfde relation in
          // de multi-set houden de relation-id erin.)
          // Voor v1 simplisme: ook altijd verwijderen. Edge-cases volgen.
          nextRels.delete(relationId);
        } else {
          nextEdges.add(id);
          nextRels.add(relationId);
        }
        ui.setSelectedEdgeIds([...nextEdges]);
        ui.setSelectedRelationIds([...nextRels]);
        if (wasInSet) {
          if (ui.selectedRelationId === relationId) {
            useUi.setState({
              selectedRelationId: null,
              selectedRelationInstanceId: null,
              selectedWaypoint: null,
            });
          }
        } else {
          useUi.setState({
            selectedRelationId: relationId,
            selectedRelationInstanceId: id,
            selectedConceptId: null,
            selectedInstanceId: null,
            selectedDiagramId: null,
            selectedWaypoint: null,
          });
        }
        return;
      }

      if (!selected) {
        // Plain klik op niet-geselecteerde edge: vervang selectie.
        e.stopPropagation();
        e.preventDefault();
        swallowSynthClick();
        const ui = useUi.getState();
        ui.selectRelation(relationId);
        // selectRelation wist selectedRelationInstanceId; daarna direct
        // herzetten met de entry-key zodat AttributePanel ook instance-
        // scope-attrs kan resolven.
        useUi.setState({
          selectedRelationInstanceId: id,
        });
        // Visuele highlight via selectedEdgeIds met deze ene entry-key.
        ui.setSelectedEdgeIds([id]);
        return;
      }

      if (useSession.getState().readOnly) return;

      // ── Pad 3: selected + plain klik + mogelijk drag → bend-insert ──
      e.preventDefault();
      e.stopPropagation();

      const startScreen = { x: e.clientX, y: e.clientY };
      const startFlow = reactFlow.screenToFlowPosition(startScreen);
      let inserted = false;
      let wpIndex = -1;
      const DRAG_THRESHOLD_PX2 = 9; // 3 pixels squared
      // Snapshot for the user-undo entry — we restore the waypoints
      // exactly as they were before the bend was created.
      const beforeWaypoints = waypoints.map((p) => ({ x: p.x, y: p.y }));

      const insertWaypointAt = (flow: Point) => {
        const points: Point[] = [
          { x: sourceX, y: sourceY },
          ...waypoints,
          { x: targetX, y: targetY },
        ];
        const segIdx = nearestSegmentInsertIndex(points, flow.x, flow.y);
        wpIndex = Math.max(0, Math.min(waypoints.length, segIdx - 1));
        const next = [...waypoints];
        next.splice(wpIndex, 0, { x: flow.x, y: flow.y });
        useWorkspaceData.getState().setRelationWaypointsOnCanvas(workspaceId, branchId, id, next);
        inserted = true;
      };

      const onMove = (ev: MouseEvent) => {
        if (!inserted) {
          const dx = ev.clientX - startScreen.x;
          const dy = ev.clientY - startScreen.y;
          if (dx * dx + dy * dy < DRAG_THRESHOLD_PX2) return;
          insertWaypointAt(startFlow);
        }
        const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
        const live =
          useWorkspaceData
            .getState()
            .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[id]
            ?.waypoints ?? [];
        const updated = live.map((p, i) => (i === wpIndex ? { x: f.x, y: f.y } : p));
        useWorkspaceData
          .getState()
          .setRelationWaypointsOnCanvas(workspaceId, branchId, id, updated);
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        if (!inserted) return;
        const afterWaypoints =
          useWorkspaceData
            .getState()
            .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[id]
            ?.waypoints ?? [];
        pushWaypointsUndo(workspaceId, branchId, id, beforeWaypoints, afterWaypoints);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [id, reactFlow, selected, sourceX, sourceY, targetX, targetY, waypoints, workspaceId, branchId],
  );

  /**
   * Drag a single existing waypoint. Same mousemove pattern as line-grab,
   * but the index is fixed up-front and we don't need to insert a new
   * point.
   */
  const onWaypointMouseDown = useCallback(
    (idx: number) => (e: React.MouseEvent<SVGCircleElement>) => {
      if (e.button !== 0) return;
      if (!workspaceId || !branchId) return;
      e.preventDefault();
      e.stopPropagation();
      const beforeWaypoints =
        useWorkspaceData
          .getState()
          .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")
          ?.viewState[id]?.waypoints?.map((p) => ({ x: p.x, y: p.y })) ?? [];
      const onMove = (ev: MouseEvent) => {
        const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
        const live =
          useWorkspaceData
            .getState()
            .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[id]
            ?.waypoints ?? [];
        const updated = live.map((p, i) => (i === idx ? { x: f.x, y: f.y } : p));
        useWorkspaceData
          .getState()
          .setRelationWaypointsOnCanvas(workspaceId, branchId, id, updated);
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        const afterWaypoints =
          useWorkspaceData
            .getState()
            .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[id]
            ?.waypoints ?? [];
        pushWaypointsUndo(workspaceId, branchId, id, beforeWaypoints, afterWaypoints);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [id, reactFlow, workspaceId, branchId],
  );

  /**
   * Double-click a waypoint to remove it — useful when the user wants to
   * straighten a line again without manually dragging the bend back onto
   * the source-target axis.
   */
  const onWaypointDoubleClick = useCallback(
    (idx: number) => (e: React.MouseEvent) => {
      if (!workspaceId || !branchId) return;
      e.preventDefault();
      e.stopPropagation();
      const live =
        useWorkspaceData
          .getState()
          .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[id]
          ?.waypoints ?? [];
      const next = live.filter((_, i) => i !== idx);
      useWorkspaceData
        .getState()
        .setRelationWaypointsOnCanvas(workspaceId, branchId, id, next.length === 0 ? null : next);
    },
    [id, workspaceId, branchId],
  );

  /**
   * Drag the source attachment circle to move the line's start angle.
   * The cursor is projected onto the nearest point on the node's actual
   * border (read from the live DOM each move) so the circle stays on the
   * border throughout the gesture, even when crossing a corner.
   */
  const onSourceAttachmentMouseDown = useCallback(
    (e: React.MouseEvent<SVGCircleElement>) => {
      if (e.button !== 0) return;
      if (!workspaceId || !branchId) return;
      e.preventDefault();
      e.stopPropagation();
      // Snapshot the angles at the start of the gesture so we can push a
      // single undo entry on release.
      const wsAtStart = useWorkspaceData.getState();
      const beforeVe = wsAtStart.store.getDiagram(wsAtStart.activeDiagramId ?? "")?.viewState[id];
      const before = {
        sourceAngle: beforeVe?.sourceAngle,
        targetAngle: beforeVe?.targetAngle,
      };
      const onMove = (ev: MouseEvent) => {
        // Live rect: prevents the circle "falling off" when the angle
        // crosses a corner and React Flow re-positions the handle.
        const liveRect = readLiveNodeRect(reactFlow, source);
        if (!liveRect) return;
        const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
        const newAngle = nearestBorderAngle(liveRect, f.x, f.y);
        const ws = useWorkspaceData.getState();
        const existing = ws.store.getDiagram(ws.activeDiagramId ?? "")?.viewState[id];
        ws.setRelationAnglesOnCanvas(workspaceId, branchId, id, newAngle, existing?.targetAngle);
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        const wsEnd = useWorkspaceData.getState();
        const afterVe = wsEnd.store.getDiagram(wsEnd.activeDiagramId ?? "")?.viewState[id];
        const after = {
          sourceAngle: afterVe?.sourceAngle,
          targetAngle: afterVe?.targetAngle,
        };
        pushAngleUndo(workspaceId, branchId, id, before, after);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [id, source, reactFlow, workspaceId, branchId],
  );

  /** Same as onSourceAttachmentMouseDown but for the target endpoint. */
  const onTargetAttachmentMouseDown = useCallback(
    (e: React.MouseEvent<SVGCircleElement>) => {
      if (e.button !== 0) return;
      if (!workspaceId || !branchId) return;
      e.preventDefault();
      e.stopPropagation();
      const wsAtStart = useWorkspaceData.getState();
      const beforeVe = wsAtStart.store.getDiagram(wsAtStart.activeDiagramId ?? "")?.viewState[id];
      const before = {
        sourceAngle: beforeVe?.sourceAngle,
        targetAngle: beforeVe?.targetAngle,
      };
      const onMove = (ev: MouseEvent) => {
        const liveRect = readLiveNodeRect(reactFlow, target);
        if (!liveRect) return;
        const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
        const newAngle = nearestBorderAngle(liveRect, f.x, f.y);
        const ws = useWorkspaceData.getState();
        const existing = ws.store.getDiagram(ws.activeDiagramId ?? "")?.viewState[id];
        ws.setRelationAnglesOnCanvas(workspaceId, branchId, id, existing?.sourceAngle, newAngle);
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        const wsEnd = useWorkspaceData.getState();
        const afterVe = wsEnd.store.getDiagram(wsEnd.activeDiagramId ?? "")?.viewState[id];
        const after = {
          sourceAngle: afterVe?.sourceAngle,
          targetAngle: afterVe?.targetAngle,
        };
        pushAngleUndo(workspaceId, branchId, id, before, after);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [id, target, reactFlow, workspaceId, branchId],
  );

  // Show attachment circles ALLEEN wanneer de edge zelf geselecteerd is.
  // De eerdere "ook bij geselecteerde-connected-node"-tak gaf veel
  // visuele ruis: zodra je één node selecteerde verschenen alle oranje
  // aanhechtingsbolletjes van alle inkomende/uitgaande relaties tegelijk.
  // De edge krijgt zIndex 1000 bij selectie (Canvas buildEdges) zodat
  // de circles alsnog bovenop renderen.
  const showSourceAttachment = !!selected;
  const showTargetAttachment = !!selected;
  void selectedInstanceId;

  // User-overridden line colour replaces the relation-type default stroke.
  const lineColor = d?.lineColor;
  const lineStyle = d?.lineStyle as "solid" | "dashed" | "dotted" | "none" | undefined;
  // strokeDasharray: 'dashed' = 6,4; 'dotted' = 1.5,4. The 'solid' value
  // explicitly clears any inherited dasharray (some relation-types like
  // Realization have a default dashed pattern via React Flow's edge-style
  // — user choosing "solid" should override that).
  const dashFor = (s: typeof lineStyle): string | undefined => {
    if (s === "dashed") return "6 4";
    if (s === "dotted") return "1.5 4";
    if (s === "solid") return "0";
    return undefined;
  };
  const dash = dashFor(lineStyle);
  // "none" lineStyle = onzichtbare lijn. We zetten stroke transparent
  // i.p.v. stroke-width 0 zodat de unsichtbare overlay-path eronder
  // (voor bend-clicks) blijft werken — alleen de zichtbare BaseEdge
  // verdwijnt.
  const isLineNone = lineStyle === "none";
  const styleWithColour: React.CSSProperties = {
    ...style,
    ...(lineColor ? { stroke: lineColor } : {}),
    ...(dash !== undefined ? { strokeDasharray: dash } : {}),
    ...(isLineNone ? { stroke: "transparent" } : {}),
  };

  // M6.5-W11 focus-ring (slice B): één keymap-subscription voor de
  // edge + z'n labels. Granulair zou per label een aparte subscription
  // werken maar dat kost extra hook-slots; één subscriptie met lokale
  // discriminatie is genoeg.
  const focusedCanvasId = useKeymap((s) => (s.scope === "canvas" ? s.focusedCanvasId : null));
  const isEdgeFocused = focusedCanvasId === id;
  const isMiddleLabelFocused = focusedCanvasId === edgeLabelFocusId(id, "middle");
  const isStartLabelFocused = focusedCanvasId === edgeLabelFocusId(id, "start");
  const isEndLabelFocused = focusedCanvasId === edgeLabelFocusId(id, "end");

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={styleWithColour}
        {...(markerStart ? { markerStart } : {})}
        {...(markerEnd ? { markerEnd } : {})}
      />
      {isEdgeFocused && (
        <path
          d={edgePath}
          fill="none"
          stroke="#000"
          strokeWidth={4}
          strokeDasharray="6 4"
          style={{ pointerEvents: "none" }}
        />
      )}
      {/* Invisible thick overlay catches mouse events along the full
          stroke. Without it the user has to land precisely on the 1.5px
          line — much too fiddly for click+drag-to-bend. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        style={{ cursor: "crosshair", pointerEvents: "stroke" }}
        onMouseDown={onLineMouseDown}
      />
      {/* Waypoints are visible only when the edge is selected, so an
          unselected line stays clean. Click selects a waypoint (so the
          canvas's Delete-keybind can remove it), drag moves it,
          double-click removes it inline. */}
      {selected &&
        !readOnly &&
        waypoints.map((wp, idx) => {
          const isWpSelected = selectedWaypoint?.edgeId === id && selectedWaypoint.index === idx;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
            <circle
              key={`${id}-wp-${wp.x},${wp.y}`}
              cx={wp.x}
              cy={wp.y}
              r={isWpSelected ? 7 : 6}
              fill={isWpSelected ? "#2563eb" : "#ffffff"}
              stroke="#2563eb"
              strokeWidth={2}
              style={{ cursor: "move" }}
              onClick={(e) => {
                e.stopPropagation();
                useUi.getState().selectWaypoint(id, idx);
              }}
              onMouseDown={onWaypointMouseDown(idx)}
              onDoubleClick={onWaypointDoubleClick(idx)}
            />
          );
        })}
      {/* Attachment-point handles: visible when the connected node is
          selected. Dragging them rotates the angle at which this edge
          leaves/enters the node border. */}
      {showSourceAttachment && !readOnly && (
        // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
        <circle
          className="nodrag nopan"
          data-edge-attachment="source"
          cx={effSourceX}
          cy={effSourceY}
          r={6}
          fill="#ffffff"
          stroke="#f59e0b"
          strokeWidth={2}
          style={{ cursor: "crosshair", pointerEvents: "all" }}
          onMouseDown={onSourceAttachmentMouseDown}
        />
      )}
      {showTargetAttachment && !readOnly && (
        // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
        <circle
          className="nodrag nopan"
          data-edge-attachment="target"
          cx={effTargetX}
          cy={effTargetY}
          r={6}
          fill="#ffffff"
          stroke="#f59e0b"
          strokeWidth={2}
          style={{ cursor: "crosshair", pointerEvents: "all" }}
          onMouseDown={onTargetAttachmentMouseDown}
        />
      )}
      {/* Sharp Concept-Model dual-end labels (forward + reverse).
          - Tekst staat altijd geroteerd in de richting van het lokale
            segment (zoals een ER-diagram dat hoort).
          - Positie = default-anchor nabij elk uiteinde + vrije 2D-offset
            uit viewState (sourceLabelOffset / targetLabelOffset). User
            kan 'm overal naartoe slepen, rotatie blijft de lijn-richting.
          - Cmd/Ctrl/Alt + sleep past de breedte aan; tekst wraps over
            meerdere regels via <tspan>.
          - Blauwe drag-handle naast de tekst voor visuele affordance. */}
      {(() => {
        const dual = d?.dualEndLabels;
        if (!dual) return null;
        const totalDx = effTargetX - effSourceX;
        const totalDy = effTargetY - effSourceY;
        const fl = Math.hypot(totalDx, totalDy) || 1;
        const fallbackUnit = { ux: totalDx / fl, uy: totalDy / fl };
        const positionToUnit = (
          pos: Position | undefined,
          isSource: boolean,
        ): { ux: number; uy: number } | null => {
          const sign = isSource ? 1 : -1;
          if (pos === Position.Right) return { ux: 1 * sign, uy: 0 };
          if (pos === Position.Left) return { ux: -1 * sign, uy: 0 };
          if (pos === Position.Bottom) return { ux: 0, uy: 1 * sign };
          if (pos === Position.Top) return { ux: 0, uy: -1 * sign };
          return null;
        };
        const fwdSeg = (() => {
          if (waypoints.length > 0) {
            const wp = waypoints[0];
            if (wp) {
              const dx2 = wp.x - effSourceX;
              const dy2 = wp.y - effSourceY;
              const l = Math.hypot(dx2, dy2) || 1;
              return { ux: dx2 / l, uy: dy2 / l };
            }
          }
          if (routing === "smoothstep" || routing === "step" || !routing) {
            return positionToUnit(effSourcePosition, true) ?? fallbackUnit;
          }
          return fallbackUnit;
        })();
        const revSeg = (() => {
          if (waypoints.length > 0) {
            const wp = waypoints[waypoints.length - 1];
            if (wp) {
              const dx2 = effTargetX - wp.x;
              const dy2 = effTargetY - wp.y;
              const l = Math.hypot(dx2, dy2) || 1;
              return { ux: dx2 / l, uy: dy2 / l };
            }
          }
          if (routing === "smoothstep" || routing === "step" || !routing) {
            return positionToUnit(effTargetPosition, false) ?? fallbackUnit;
          }
          return fallbackUnit;
        })();
        const defaultInset = 18;
        const fwdDefault = {
          x: effSourceX + fwdSeg.ux * defaultInset,
          y: effSourceY + fwdSeg.uy * defaultInset,
        };
        const revDefault = {
          x: effTargetX - revSeg.ux * defaultInset,
          y: effTargetY - revSeg.uy * defaultInset,
        };
        const fwdOffset = d?.sourceLabelOffset ?? { x: 0, y: 0 };
        const revOffset = d?.targetLabelOffset ?? { x: 0, y: 0 };
        const fwdAnchor = { x: fwdDefault.x + fwdOffset.x, y: fwdDefault.y + fwdOffset.y };
        const revAnchor = { x: revDefault.x + revOffset.x, y: revDefault.y + revOffset.y };

        const flipReadable = (raw: number) => {
          if (raw > 90 || raw < -90) {
            return { angle: raw > 0 ? raw - 180 : raw + 180, flipped: true };
          }
          return { angle: raw, flipped: false };
        };
        const fwdNorm = flipReadable((Math.atan2(fwdSeg.uy, fwdSeg.ux) * 180) / Math.PI);
        const revNorm = flipReadable((Math.atan2(revSeg.uy, revSeg.ux) * 180) / Math.PI);
        const fwdTextAnchor: "start" | "end" = fwdNorm.flipped ? "end" : "start";
        const revTextAnchor: "start" | "end" = revNorm.flipped ? "start" : "end";

        // Geschatte gemiddelde tekenbreedte op fontSize 10 in
        // sans-serif (Inter / system-ui). 6 was te ruim — bij langere
        // tekst stond de pill ver van het werkelijke tekst-einde
        // omdat de schatting een breder bounding-box voorspelde dan
        // de browser daadwerkelijk rendert. 5.2 ligt dichter bij de
        // werkelijke gemiddelde glyph-breedte.
        const charWidth = 5.2;
        const lineHeightPx = 12;
        const wrapToLines = (text: string, maxWidth: number | undefined): string[] => {
          if (!maxWidth) return [text];
          const maxChars = Math.max(3, Math.floor(maxWidth / charWidth));
          const words = text.split(/\s+/);
          const out: string[] = [];
          let cur = "";
          for (const word of words) {
            const candidate = cur ? `${cur} ${word}` : word;
            if (candidate.length > maxChars && cur) {
              out.push(cur);
              cur = word;
            } else {
              cur = candidate;
            }
          }
          if (cur) out.push(cur);
          return out.length > 0 ? out : [text];
        };
        const fwdLines = wrapToLines(dual.forward, d?.sourceLabelWidth);
        const revLines = wrapToLines(dual.reverse, d?.targetLabelWidth);
        const fwdWidthEst = Math.max(...fwdLines.map((l) => l.length * charWidth), 0);
        const revWidthEst = Math.max(...revLines.map((l) => l.length * charWidth), 0);

        const beginEndLabelDrag = (side: "source" | "target") => (e: React.MouseEvent) => {
          if (e.button !== 0) return;
          if (!workspaceId || !branchId) return;
          e.preventDefault();
          e.stopPropagation();
          const ws = useWorkspaceData.getState();
          if (e.metaKey || e.ctrlKey || e.altKey) {
            const widthBefore = side === "source" ? d?.sourceLabelWidth : d?.targetLabelWidth;
            const startWidth = widthBefore ?? (side === "source" ? fwdWidthEst : revWidthEst) ?? 80;
            const startScreen = { x: e.clientX, y: e.clientY };
            const startFlow = reactFlow.screenToFlowPosition(startScreen);
            let rafIdW: number | null = null;
            let pendingW = startWidth;
            let lastAppliedW = startWidth;
            const onMoveW = (ev: MouseEvent) => {
              const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
              const delta = f.x - startFlow.x;
              pendingW = Math.max(40, Math.round(startWidth + delta));
              if (rafIdW === null) {
                rafIdW = requestAnimationFrame(() => {
                  rafIdW = null;
                  if (pendingW !== lastAppliedW) {
                    lastAppliedW = pendingW;
                    ws.setRelationEndLabelWidthOnCanvas(workspaceId, branchId, id, side, pendingW);
                  }
                });
              }
            };
            const onUpW = () => {
              window.removeEventListener("mousemove", onMoveW);
              window.removeEventListener("mouseup", onUpW);
              if (rafIdW !== null) cancelAnimationFrame(rafIdW);
              if (pendingW !== lastAppliedW) {
                ws.setRelationEndLabelWidthOnCanvas(workspaceId, branchId, id, side, pendingW);
              }
              // Eén undo-entry voor de hele drag-sessie.
              if (pendingW !== widthBefore) {
                useWorkspaceData.getState().pushUserUndo({
                  label: side === "source" ? "Forward-label breedte" : "Reverse-label breedte",
                  undo: () =>
                    useWorkspaceData
                      .getState()
                      .setRelationEndLabelWidthOnCanvas(
                        workspaceId,
                        branchId,
                        id,
                        side,
                        widthBefore ?? null,
                      ),
                  redo: () =>
                    useWorkspaceData
                      .getState()
                      .setRelationEndLabelWidthOnCanvas(workspaceId, branchId, id, side, pendingW),
                });
              }
            };
            window.addEventListener("mousemove", onMoveW);
            window.addEventListener("mouseup", onUpW);
            return;
          }
          // Vrije 2D-drag — verplaats het label, rotatie blijft langs
          // de lijn-richting. rAF-throttle voor soepele drag, één
          // undo-entry voor de hele gesture.
          const baseOffset = side === "source" ? { ...fwdOffset } : { ...revOffset };
          const offsetBefore = side === "source" ? d?.sourceLabelOffset : d?.targetLabelOffset;
          const startScreen = { x: e.clientX, y: e.clientY };
          const startFlow = reactFlow.screenToFlowPosition(startScreen);
          let rafId: number | null = null;
          let pending = baseOffset;
          let lastApplied = baseOffset;
          const onMove = (ev: MouseEvent) => {
            const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
            pending = {
              x: baseOffset.x + (f.x - startFlow.x),
              y: baseOffset.y + (f.y - startFlow.y),
            };
            if (rafId === null) {
              rafId = requestAnimationFrame(() => {
                rafId = null;
                if (pending.x !== lastApplied.x || pending.y !== lastApplied.y) {
                  lastApplied = pending;
                  ws.setRelationEndLabelOffsetOnCanvas(workspaceId, branchId, id, side, pending);
                }
              });
            }
          };
          const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            if (rafId !== null) cancelAnimationFrame(rafId);
            if (pending.x !== lastApplied.x || pending.y !== lastApplied.y) {
              ws.setRelationEndLabelOffsetOnCanvas(workspaceId, branchId, id, side, pending);
            }
            // Eén undo-entry voor de hele drag-sessie.
            const movedFrom = offsetBefore ?? null;
            if (!movedFrom || movedFrom.x !== pending.x || movedFrom.y !== pending.y) {
              useWorkspaceData.getState().pushUserUndo({
                label: side === "source" ? "Forward-label positie" : "Reverse-label positie",
                undo: () =>
                  useWorkspaceData
                    .getState()
                    .setRelationEndLabelOffsetOnCanvas(workspaceId, branchId, id, side, movedFrom),
                redo: () =>
                  useWorkspaceData
                    .getState()
                    .setRelationEndLabelOffsetOnCanvas(workspaceId, branchId, id, side, pending),
              });
            }
          };
          window.addEventListener("mousemove", onMove);
          window.addEventListener("mouseup", onUp);
        };

        const labelTextStyle: React.CSSProperties = {
          fontSize: 10,
          fill: "#475569",
          pointerEvents: "none",
          userSelect: "none",
        };
        const hitRectStyle: React.CSSProperties = {
          fill: "transparent",
          cursor: "move",
          pointerEvents: "all",
        };
        const fwdHeight = fwdLines.length * lineHeightPx + 4;
        const revHeight = revLines.length * lineHeightPx + 4;
        const fwdRectX =
          fwdTextAnchor === "start" ? fwdAnchor.x - 2 : fwdAnchor.x - fwdWidthEst - 2;
        const revRectX =
          revTextAnchor === "start" ? revAnchor.x - 2 : revAnchor.x - revWidthEst - 2;

        // Resize-pill (zichtbaar als de edge geselecteerd is, analoog
        // aan de pill op het standaard relatie-label op regel 1304+).
        // Klein blauw bolletje is vervangen door dezelfde pill-met-↔-glyph
        // zodat 't gedrag identiek voelt: drag = breedte aanpassen,
        // tekst wraps over meerdere regels.
        const PILL_W = 22;
        const PILL_H = 16;
        // Extra hit-padding rondom de zichtbare pill voor robuuste
        // klik-target — de zichtbare pill blijft compact maar gebruikers
        // raken 'm ook bij ietwat onzorgvuldige clicks. Same trick als
        // de standaard label-pill (die zit in HTML met natuurlijke
        // click-tolerantie; SVG vereist een grotere bounding box).
        const PILL_HIT_PAD = 4;
        // Pill aan de GROEIENDE rand van de tekst (waar de tekst zich
        // uitbreidt als de breedte toeneemt):
        // - "start"-anchor: tekst groeit naar rechts → pill rechts ervan
        // - "end"-anchor: tekst groeit naar links → pill links ervan
        // Dat houdt de pill bij de visuele rand die meebeweegt met de
        // breedte-aanpassing. Anders staat de pill stil terwijl de
        // tekst om de anchor heen krimpt/groeit en lijkt 't kapot.
        const PILL_GAP = 2;
        const fwdPillX =
          fwdTextAnchor === "start"
            ? fwdAnchor.x + fwdWidthEst + PILL_GAP
            : fwdAnchor.x - fwdWidthEst - PILL_GAP - PILL_W;
        const fwdPillY = fwdAnchor.y - 6 - PILL_H / 2 - 4;
        const revPillX =
          revTextAnchor === "start"
            ? revAnchor.x + revWidthEst + PILL_GAP
            : revAnchor.x - revWidthEst - PILL_GAP - PILL_W;
        const revPillY = revAnchor.y + 6 + 4 - PILL_H / 2;

        const beginWidthResize = (side: "source" | "target") => (e: React.MouseEvent) => {
          if (e.button !== 0) return;
          if (!workspaceId || !branchId) return;
          e.preventDefault();
          e.stopPropagation();
          const ws = useWorkspaceData.getState();
          const widthBefore = side === "source" ? d?.sourceLabelWidth : d?.targetLabelWidth;
          const startWidth = widthBefore ?? (side === "source" ? fwdWidthEst : revWidthEst) ?? 80;
          const startFlow = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
          // Drag-richting volgt de TEKST-as, niet de scherm-x. Voor een
          // verticaal/diagonaal label betekent dat: slepen in de richting
          // waar de tekst groeit = breedte vergroten; tegengesteld =
          // verkleinen. Bij flipped anchor (text-anchor=end) groeit de
          // tekst in -rotated-x, dus omdraaien.
          const angleDeg = side === "source" ? fwdNorm.angle : revNorm.angle;
          const flipped = (side === "source" ? fwdTextAnchor : revTextAnchor) === "end";
          const angleRad = (angleDeg * Math.PI) / 180;
          const dirX = Math.cos(angleRad) * (flipped ? -1 : 1);
          const dirY = Math.sin(angleRad) * (flipped ? -1 : 1);
          // rAF-throttle: setViewElementState bumpt de store-revisie en
          // forceert een edge-rerender. Mousemove vuurt 100+ keer/sec
          // op trackpads — dat geeft merkbaar lag. Door per animation-
          // frame max één update te doen blijft 't soepel (60fps).
          let rafId: number | null = null;
          let pendingWidth = startWidth;
          let lastApplied = startWidth;
          const onMove = (ev: MouseEvent) => {
            const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
            const dx = f.x - startFlow.x;
            const dy = f.y - startFlow.y;
            const delta = dx * dirX + dy * dirY;
            pendingWidth = Math.max(40, Math.round(startWidth + delta));
            if (rafId === null) {
              rafId = requestAnimationFrame(() => {
                rafId = null;
                if (pendingWidth !== lastApplied) {
                  lastApplied = pendingWidth;
                  ws.setRelationEndLabelWidthOnCanvas(
                    workspaceId,
                    branchId,
                    id,
                    side,
                    pendingWidth,
                  );
                }
              });
            }
          };
          const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            if (rafId !== null) cancelAnimationFrame(rafId);
            // Eind-state forceren als laatste pending-waarde nog niet is
            // toegepast (rAF kan tussen laatste mousemove en mouseup nog
            // niet hebben gevuurd).
            if (pendingWidth !== lastApplied) {
              ws.setRelationEndLabelWidthOnCanvas(workspaceId, branchId, id, side, pendingWidth);
            }
            // Eén undo-entry voor de hele drag-sessie.
            if (pendingWidth !== widthBefore) {
              useWorkspaceData.getState().pushUserUndo({
                label: side === "source" ? "Forward-label breedte" : "Reverse-label breedte",
                undo: () =>
                  useWorkspaceData
                    .getState()
                    .setRelationEndLabelWidthOnCanvas(
                      workspaceId,
                      branchId,
                      id,
                      side,
                      widthBefore ?? null,
                    ),
                redo: () =>
                  useWorkspaceData
                    .getState()
                    .setRelationEndLabelWidthOnCanvas(
                      workspaceId,
                      branchId,
                      id,
                      side,
                      pendingWidth,
                    ),
              });
            }
          };
          window.addEventListener("mousemove", onMove);
          window.addEventListener("mouseup", onUp);
        };
        const onPillDoubleClick = (side: "source" | "target") => (e: React.MouseEvent) => {
          if (!workspaceId || !branchId) return;
          e.preventDefault();
          e.stopPropagation();
          useWorkspaceData
            .getState()
            .setRelationEndLabelWidthOnCanvas(workspaceId, branchId, id, side, null);
        };
        const renderResizePill = (
          side: "source" | "target",
          x: number,
          y: number,
        ): React.ReactNode => (
          // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
          <g
            onMouseDown={beginWidthResize(side)}
            onDoubleClick={onPillDoubleClick(side)}
            style={{ cursor: "ew-resize", pointerEvents: "all" }}
          >
            <title>{t("canvas.edgeLabel.widthPillTitle")}</title>
            {/* Onzichtbare hit-padding zodat clicks die net naast de
                zichtbare pill landen ook de drag triggeren. */}
            <rect
              x={x - PILL_HIT_PAD}
              y={y - PILL_HIT_PAD}
              width={PILL_W + PILL_HIT_PAD * 2}
              height={PILL_H + PILL_HIT_PAD * 2}
              fill="transparent"
              style={{ pointerEvents: "all" }}
            />
            <rect
              x={x}
              y={y}
              width={PILL_W}
              height={PILL_H}
              rx={4}
              ry={4}
              fill="#2563eb"
              stroke="white"
              strokeWidth={1}
              style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))", pointerEvents: "none" }}
            />
            <text
              x={x + PILL_W / 2}
              y={y + PILL_H / 2 + 3}
              textAnchor="middle"
              fill="white"
              fontSize={11}
              fontWeight={700}
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              ⇔
            </text>
          </g>
        );
        return (
          <>
            {dual.forward && (
              // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
              <g
                transform={`rotate(${fwdNorm.angle} ${fwdAnchor.x} ${fwdAnchor.y})`}
                onMouseDown={beginEndLabelDrag("source")}
              >
                <rect
                  x={fwdRectX}
                  y={fwdAnchor.y - fwdHeight}
                  width={fwdWidthEst + 4}
                  height={fwdHeight}
                  style={{
                    ...hitRectStyle,
                    ...(isStartLabelFocused
                      ? {
                          stroke: "#000",
                          strokeWidth: 3,
                          strokeDasharray: "6 4",
                        }
                      : {}),
                  }}
                />
                <text
                  x={fwdAnchor.x}
                  y={fwdAnchor.y - 6}
                  textAnchor={fwdTextAnchor}
                  style={labelTextStyle}
                >
                  {fwdLines.map((line, i) => (
                    <tspan
                      // biome-ignore lint/suspicious/noArrayIndexKey: line-index is stable per-render
                      key={i}
                      x={fwdAnchor.x}
                      dy={i === 0 ? -(fwdLines.length - 1) * lineHeightPx : lineHeightPx}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                {selected && renderResizePill("source", fwdPillX, fwdPillY)}
              </g>
            )}
            {dual.reverse && (
              // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop
              <g
                transform={`rotate(${revNorm.angle} ${revAnchor.x} ${revAnchor.y})`}
                onMouseDown={beginEndLabelDrag("target")}
              >
                <rect
                  x={revRectX}
                  y={revAnchor.y + 2}
                  width={revWidthEst + 4}
                  height={revHeight}
                  style={{
                    ...hitRectStyle,
                    ...(isEndLabelFocused
                      ? {
                          stroke: "#000",
                          strokeWidth: 3,
                          strokeDasharray: "6 4",
                        }
                      : {}),
                  }}
                />
                <text
                  x={revAnchor.x}
                  y={revAnchor.y + 6}
                  textAnchor={revTextAnchor}
                  style={{ ...labelTextStyle, dominantBaseline: "hanging" }}
                >
                  {revLines.map((line, i) => (
                    <tspan
                      // biome-ignore lint/suspicious/noArrayIndexKey: line-index is stable per-render
                      key={i}
                      x={revAnchor.x}
                      dy={i === 0 ? 0 : lineHeightPx}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                {selected && renderResizePill("target", revPillX, revPillY)}
              </g>
            )}
          </>
        );
      })()}
      {/* Render de label-container ook als `editing` (zonder naam): zo werkt
          F2 / inline-edit op een nog-naamloze relatie — anders is er geen
          aangrijppunt om de input te tonen. */}
      {(label || editing) && !d?.dualEndLabels && (
        <EdgeLabelRenderer>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: React-Flow edge-interactie-target; selectie + keyboard lopen via de React-Flow-canvas-navigatie, geen per-element tab-stop */}
          <div
            className="nodrag nopan absolute"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX + labelOffset.x}px, ${labelY + labelOffset.y}px)`,
              borderRadius: 3,
              ...(labelStyle ?? {}),
              ...(labelBgStyle ?? {}),
              padding: Array.isArray(labelBgPadding)
                ? `${labelBgPadding[1] ?? 2}px ${labelBgPadding[0] ?? 4}px`
                : undefined,
              pointerEvents: "all",
              cursor: "move",
              userSelect: "none",
              // M6.5-W11: zwart-dashed outline op het label wanneer 't focus heeft.
              ...(isMiddleLabelFocused ? { outline: "3px dashed #000", outlineOffset: 2 } : {}),
              // Labels liggen altijd boven de lijnen — ook boven
              // kruisende lijnen van andere edges. Edges zelf zitten
              // op 5000 (default) of 6000 (selected); labels op 7000
              // staan dus áltijd erboven. Dichtgeklapte containers
              // (10000) blijven nog hoger zodat ze label én lijn
              // erbinnen visueel afdekken.
              zIndex: 7000,
              // When the user has Cmd/Ctrl-dragged the label, we apply a
              // hard `maxWidth` so long labels wrap onto multiple lines.
              // `whiteSpace: normal` overrides the default single-line
              // edge-label rendering; `textAlign: center` keeps the
              // wrapped lines visually centred under the edge.
              ...(typeof d?.labelMaxWidth === "number" && d.labelMaxWidth > 0
                ? {
                    maxWidth: `${d.labelMaxWidth}px`,
                    whiteSpace: "normal" as const,
                    textAlign: d?.labelTextAlign ?? "center",
                  }
                : d?.labelTextAlign
                  ? { textAlign: d.labelTextAlign }
                  : {}),
              // hyphenate-modus: probeer hyphenation eerst, val terug
              // op mid-word break voor woorden waarvoor de browser
              // geen syllabe-patroon kent (eigennamen, acroniemen, etc.).
              ...(d?.labelWrap === "hyphenate"
                ? { hyphens: "auto" as const, overflowWrap: "anywhere" as const }
                : d?.labelWrap === "break-anywhere"
                  ? { overflowWrap: "anywhere" as const }
                  : {}),
            }}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              if (!workspaceId || !branchId) return;
              e.preventDefault();
              e.stopPropagation();
              // Modifier-drag ⇒ resize: any of Cmd / Ctrl / Alt
              // held at mouse-down enters width-resize mode (Shift
              // is reserved for React Flow's lasso-selection so we
              // can't grab it here). For the most reliable UX
              // there's also an explicit resize handle on the right
              // side of the label when the edge is selected — see
              // the JSX below.
              if (e.metaKey || e.ctrlKey || e.altKey) {
                const labelEl = e.currentTarget as HTMLDivElement;
                const startWidth =
                  d?.labelMaxWidth ?? Math.round(labelEl.getBoundingClientRect().width);
                const startScreen = { x: e.clientX, y: e.clientY };
                const startFlow = reactFlow.screenToFlowPosition(startScreen);
                const onMoveResize = (ev: MouseEvent) => {
                  const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
                  // Mouse-X delta in flow-coords; the label sits at the
                  // edge midpoint so right-drag widens, left-drag narrows
                  // by the same delta on each side. We use a single-side
                  // delta (cleaner mental model) — pixel-for-pixel.
                  const delta = f.x - startFlow.x;
                  const next = Math.max(40, Math.round(startWidth + delta));
                  useWorkspaceData
                    .getState()
                    .setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, id, next);
                };
                const onUpResize = () => {
                  window.removeEventListener("mousemove", onMoveResize);
                  window.removeEventListener("mouseup", onUpResize);
                };
                window.addEventListener("mousemove", onMoveResize);
                window.addEventListener("mouseup", onUpResize);
                return;
              }
              const startScreen = { x: e.clientX, y: e.clientY };
              const startFlow = reactFlow.screenToFlowPosition(startScreen);
              const baseOffset = { ...labelOffset };
              const onMove = (ev: MouseEvent) => {
                const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
                const next = {
                  x: baseOffset.x + (f.x - startFlow.x),
                  y: baseOffset.y + (f.y - startFlow.y),
                };
                useWorkspaceData
                  .getState()
                  .setRelationLabelOffsetOnCanvas(workspaceId, branchId, id, next);
              };
              const onUp = () => {
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
                const live = useWorkspaceData
                  .getState()
                  .store.getDiagram(useWorkspaceData.getState().activeDiagramId ?? "")?.viewState[
                  id
                ]?.labelOffset ?? { x: 0, y: 0 };
                pushLabelOffsetUndo(workspaceId, branchId, id, baseOffset, live);
              };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
            }}
            onDoubleClick={(e) => {
              // Double-click snapt het label terug naar standaard. Met
              // modifier (Shift / Cmd / Ctrl / Alt): snap de wrap-breedte
              // terug (volledige intrinsieke contentbreedte). Zonder
              // modifier: snap de positie terug zoals voorheen.
              if (!workspaceId || !branchId) return;
              e.preventDefault();
              e.stopPropagation();
              if (e.metaKey || e.ctrlKey || e.altKey) {
                useWorkspaceData
                  .getState()
                  .setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, id, null);
              } else {
                useWorkspaceData
                  .getState()
                  .setRelationLabelOffsetOnCanvas(workspaceId, branchId, id, null);
              }
            }}
            // Suppress the macOS Ctrl-click → context-menu detour. The
            // label has its own actions (drag, resize, reset); a browser
            // context-menu serves no purpose here and only hides the
            // resize gesture under a popup.
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            // Dubbelklik = inline-edit van het edge-label in de actieve
            // taal. Dezelfde UX als ConceptNode (dubbelklik op label).
            // We onderscheppen vóór de bestaande Cmd/Alt-doubleClick die
            // de width/offset reset zodat 'n plain-doubleclick edit
            // start; modifier-doubleclick blijft 'reset' doen.
            onDoubleClickCapture={(e) => {
              if (e.metaKey || e.ctrlKey || e.altKey) return;
              e.preventDefault();
              e.stopPropagation();
              beginLabelEdit();
            }}
            title={
              selected ? t("canvas.edgeLabel.selectedTitle") : t("canvas.edgeLabel.unselectedTitle")
            }
          >
            {editing ? (
              <input
                ref={editInputRef}
                className="rounded border border-blue-400 bg-white px-1 text-sm focus:border-blue-600 focus:outline-none"
                style={{ minWidth: 80, maxWidth: 240 }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void persistLabelEdit();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setEditing(false);
                    endLabelEdit();
                  }
                }}
                onBlur={() => void persistLabelEdit()}
              />
            ) : (
              label
            )}
            {/*
              C4 convention: the relation's `technology` attribute renders
              as a small subtitle under the main label (e.g.
              "[AWS S3 API/HTTP]"). Canvas.tsx populates `data.technology`
              only for C4-flavoured views, so this is a no-op on ArchiMate
              diagrams — keeping their visuals untouched.
            */}
            {typeof d?.technology === "string" && d.technology.length > 0 && (
              <div
                style={{
                  fontSize: "0.7em",
                  opacity: 0.7,
                  marginTop: 1,
                  lineHeight: 1.1,
                  textAlign: "center",
                }}
              >
                [{d.technology}]
              </div>
            )}
            {/*
              Resize handle — alleen zichtbaar als de edge geselecteerd
              is. Hangt buiten de rechterrand van het label zodat 'm
              niet over de tekst zit. Drag verandert maxWidth zodat
              de tekst wrapt; geen modifier-toets nodig. Dubbelklik
              op het handle reset de breedte.
            */}
            {selected && (
              <button
                type="button"
                title={t("canvas.edgeLabel.widthHandleTitle")}
                aria-label={t("canvas.edgeLabel.widthHandleAria")}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  if (!workspaceId || !branchId) return;
                  e.preventDefault();
                  e.stopPropagation();
                  // Find the parent label container so we know its
                  // current rendered width as the starting point.
                  const labelEl = (e.currentTarget as HTMLElement).closest(
                    ".nodrag",
                  ) as HTMLElement | null;
                  const startWidth =
                    d?.labelMaxWidth ??
                    (labelEl ? Math.round(labelEl.getBoundingClientRect().width) : 200);
                  const startScreen = { x: e.clientX, y: e.clientY };
                  const startFlow = reactFlow.screenToFlowPosition(startScreen);
                  const onMoveResize = (ev: MouseEvent) => {
                    const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
                    const delta = f.x - startFlow.x;
                    const next = Math.max(40, Math.round(startWidth + delta));
                    useWorkspaceData
                      .getState()
                      .setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, id, next);
                  };
                  const onUpResize = () => {
                    window.removeEventListener("mousemove", onMoveResize);
                    window.removeEventListener("mouseup", onUpResize);
                  };
                  window.addEventListener("mousemove", onMoveResize);
                  window.addEventListener("mouseup", onUpResize);
                }}
                onDoubleClick={(e) => {
                  if (!workspaceId || !branchId) return;
                  e.preventDefault();
                  e.stopPropagation();
                  useWorkspaceData
                    .getState()
                    .setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, id, null);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: -22,
                  transform: "translateY(-50%)",
                  // Groter hit-gebied (22×22, was 14×18) zodat de
                  // resize-pill makkelijker te grijpen is — vooral
                  // op trackpads / kleine pointers. Visueel schaalt
                  // het pijltje mee.
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#2563eb",
                  color: "white",
                  border: "1px solid white",
                  borderRadius: 4,
                  cursor: "ew-resize",
                  fontSize: 14,
                  lineHeight: 1,
                  fontWeight: 700,
                  pointerEvents: "all",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              >
                ⇔
              </button>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
